import { z } from "zod";
export const agentCardTypeSchema = z.enum([
    "work_card",
    "question_card",
    "permission_card",
    "action_required_card",
    "release_card",
    "completion_card",
    "failure_card",
    "stopped_card",
    "live_problem_card",
]);
export const liveAgentCardTypeSchema = z.enum([
    "work_card",
    "question_card",
    "permission_card",
    "action_required_card",
]);
export const agentCardStyleColorSchema = z.enum(["blue", "green", "orange", "red"]);
export const agentCardIconSchema = z.enum([
    "loader",
    "question",
    "permission",
    "connection",
    "rocket",
    "check",
    "alert",
    "stopped",
    "health",
    "lock",
]);
const cardIdSchema = z
    .string()
    .trim()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9][a-z0-9._:-]*$/);
const titleSchema = z.string().trim().min(1).max(500);
const detailSchema = z.string().trim().min(1).max(2_000);
export const agentCardProgressStateSchema = z.enum([
    "pending",
    "active",
    "completed",
    "blocked",
]);
export const agentCardActionSchema = z
    .object({
    id: cardIdSchema,
    label: z.string().trim().min(1).max(200),
    action: cardIdSchema,
    style: z.enum(["primary", "secondary", "destructive"]),
    href: z.url().optional(),
})
    .strict();
export const agentCardChecklistItemSchema = z
    .object({
    id: cardIdSchema,
    title: titleSchema,
    detail: detailSchema.optional(),
    state: agentCardProgressStateSchema,
})
    .strict();
export const agentCardMilestoneSchema = z
    .object({
    id: cardIdSchema,
    title: titleSchema,
    detail: detailSchema.optional(),
    state: agentCardProgressStateSchema,
    items: z.array(agentCardChecklistItemSchema).min(1).max(12),
})
    .strict()
    .superRefine((value, context) => uniqueIds(value.items, context, ["items"]));
export const agentCardChoiceOptionSchema = z
    .object({
    id: cardIdSchema,
    label: z.string().trim().min(1).max(200),
    detail: detailSchema,
    recommended: z.boolean().optional(),
})
    .strict();
export const agentCardVerificationCheckSchema = z
    .object({
    id: cardIdSchema,
    label: z.string().trim().min(1).max(200),
    status: z.enum(["passed", "failed", "not_tested", "not_needed"]),
    detail: detailSchema.optional(),
})
    .strict();
const markdownSectionSchema = z
    .object({
    id: cardIdSchema,
    type: z.literal("markdown"),
    markdown: z.string().trim().min(1).max(10_000),
    display: z.enum(["plain", "box"]),
    title: titleSchema.optional(),
    meta: z.string().trim().min(1).max(500).optional(),
    occurredAt: z.iso.datetime().optional(),
    styleColor: agentCardStyleColorSchema.optional(),
    icon: agentCardIconSchema.optional(),
})
    .strict();
const checklistSectionSchema = z
    .object({
    id: cardIdSchema,
    type: z.literal("checklist"),
    title: titleSchema.optional(),
    items: z.array(agentCardChecklistItemSchema).min(1).max(12),
})
    .strict()
    .superRefine((value, context) => uniqueIds(value.items, context, ["items"]));
const milestonesSectionSchema = z
    .object({
    id: cardIdSchema,
    type: z.literal("milestones"),
    title: titleSchema.optional(),
    items: z.array(agentCardMilestoneSchema).min(1).max(12),
})
    .strict()
    .superRefine((value, context) => {
    uniqueIds(value.items, context, ["items"]);
    if (value.items.filter(({ state }) => state === "active").length > 1) {
        context.addIssue({
            code: "custom",
            path: ["items"],
            message: "A milestones section can have at most one active milestone",
        });
    }
});
const choicesSectionSchema = z
    .object({
    id: cardIdSchema,
    type: z.literal("choices"),
    options: z.array(agentCardChoiceOptionSchema).min(2).max(5),
    customAnswer: z
        .object({
        label: z.string().trim().min(1).max(200),
        placeholder: z.string().trim().min(1).max(500),
        actionLabel: z.string().trim().min(1).max(200),
    })
        .strict()
        .optional(),
})
    .strict()
    .superRefine((value, context) => uniqueIds(value.options, context, ["options"]));
const listSectionSchema = z
    .object({
    id: cardIdSchema,
    type: z.literal("list"),
    title: titleSchema.optional(),
    items: z
        .array(z
        .object({
        id: cardIdSchema,
        text: detailSchema,
        icon: z.enum(["check", "pause", "lock"]).optional(),
    })
        .strict())
        .min(1)
        .max(20),
})
    .strict()
    .superRefine((value, context) => uniqueIds(value.items, context, ["items"]));
const verificationSectionSchema = z
    .object({
    id: cardIdSchema,
    type: z.literal("verification"),
    title: titleSchema.optional(),
    checks: z.array(agentCardVerificationCheckSchema).min(1).max(50),
})
    .strict()
    .superRefine((value, context) => uniqueIds(value.checks, context, ["checks"]));
const actionsSectionSchema = z
    .object({
    id: cardIdSchema,
    type: z.literal("actions"),
    actions: z.array(agentCardActionSchema).min(1).max(5),
})
    .strict()
    .superRefine((value, context) => uniqueIds(value.actions, context, ["actions"]));
const statsSectionSchema = z
    .object({
    id: cardIdSchema,
    type: z.literal("stats"),
    items: z
        .array(z
        .object({
        id: cardIdSchema,
        value: z.string().trim().min(1).max(100),
        label: z.string().trim().min(1).max(200),
    })
        .strict())
        .min(1)
        .max(8),
})
    .strict()
    .superRefine((value, context) => uniqueIds(value.items, context, ["items"]));
export const agentCardSectionSchema = z.lazy(() => z.discriminatedUnion("type", [
    markdownSectionSchema,
    checklistSectionSchema,
    milestonesSectionSchema,
    choicesSectionSchema,
    listSectionSchema,
    verificationSectionSchema,
    actionsSectionSchema,
    statsSectionSchema,
    z
        .object({
        id: cardIdSchema,
        type: z.literal("disclosure"),
        label: z.string().trim().min(1).max(200),
        expandedLabel: z.string().trim().min(1).max(200).optional(),
        sections: z.array(agentCardSectionSchema).min(1).max(12),
    })
        .strict()
        .superRefine((value, context) => uniqueIds(value.sections, context, ["sections"])),
]));
export const agentCardSchema = z
    .object({
    id: cardIdSchema,
    type: agentCardTypeSchema,
    revision: z.number().int().positive(),
    state: z.enum(["live", "settled"]),
    icon: agentCardIconSchema,
    title: titleSchema,
    subtitle: titleSchema,
    styleColor: agentCardStyleColorSchema,
    sections: z.array(agentCardSectionSchema).min(1).max(12),
})
    .strict()
    .superRefine((value, context) => {
    uniqueIds(value.sections, context, ["sections"]);
    if (!isLiveAgentCardType(value.type) && value.state !== "settled") {
        context.addIssue({
            code: "custom",
            path: ["state"],
            message: "Outcome cards must be settled",
        });
    }
});
const agentCardSetSchema = z
    .object({
    state: z.enum(["live", "settled"]).optional(),
    icon: agentCardIconSchema.optional(),
    title: titleSchema.optional(),
    subtitle: titleSchema.optional(),
    styleColor: agentCardStyleColorSchema.optional(),
})
    .strict();
function keyedPatchSchema(item) {
    return z
        .object({
        upsert: z.array(item).max(50).optional(),
        remove: z.array(cardIdSchema).max(50).optional(),
    })
        .strict();
}
const sectionItemsPatchSchema = z.discriminatedUnion("sectionType", [
    z
        .object({
        sectionId: cardIdSchema,
        sectionType: z.literal("checklist"),
        items: keyedPatchSchema(agentCardChecklistItemSchema),
    })
        .strict(),
    z
        .object({
        sectionId: cardIdSchema,
        sectionType: z.literal("choices"),
        items: keyedPatchSchema(agentCardChoiceOptionSchema),
    })
        .strict(),
    z
        .object({
        sectionId: cardIdSchema,
        sectionType: z.literal("actions"),
        items: keyedPatchSchema(agentCardActionSchema),
    })
        .strict(),
    z
        .object({
        sectionId: cardIdSchema,
        sectionType: z.literal("verification"),
        items: keyedPatchSchema(agentCardVerificationCheckSchema),
    })
        .strict(),
]);
const milestoneUpdateSchema = z
    .object({
    sectionId: cardIdSchema,
    milestoneId: cardIdSchema,
    set: z
        .object({
        title: titleSchema.optional(),
        detail: detailSchema.nullable().optional(),
        state: agentCardProgressStateSchema.optional(),
    })
        .strict()
        .optional(),
    items: keyedPatchSchema(agentCardChecklistItemSchema).optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (!value.set && !value.items) {
        context.addIssue({
            code: "custom",
            message: "A milestone update must change the milestone or its submilestones",
        });
    }
});
export const agentCardDeltaSchema = z
    .object({
    event: z.enum([
        "work_card.updated",
        "question_card.updated",
        "permission_card.updated",
        "action_required_card.updated",
    ]),
    cardId: cardIdSchema,
    baseRevision: z.number().int().positive(),
    revision: z.number().int().positive(),
    set: agentCardSetSchema.optional(),
    sections: keyedPatchSchema(agentCardSectionSchema).optional(),
    sectionItems: z.array(sectionItemsPatchSchema).max(20).optional(),
    milestoneUpdates: z.array(milestoneUpdateSchema).max(20).optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.revision <= value.baseRevision) {
        context.addIssue({
            code: "custom",
            path: ["revision"],
            message: "A card update must advance the revision",
        });
    }
});
export function isLiveAgentCardType(type) {
    return liveAgentCardTypeSchema.safeParse(type).success;
}
export function applyAgentCardDelta(cardValue, deltaValue) {
    const card = agentCardSchema.parse(cardValue);
    const delta = agentCardDeltaSchema.parse(deltaValue);
    if (card.state !== "live" || !isLiveAgentCardType(card.type)) {
        throw new Error(`${card.type} is immutable after it settles`);
    }
    if (delta.event !== `${card.type}.updated`) {
        throw new Error(`${delta.event} cannot update ${card.type}`);
    }
    if (delta.cardId !== card.id)
        throw new Error("Card update targets a different card");
    if (delta.baseRevision !== card.revision) {
        throw new Error(`Card update expects revision ${delta.baseRevision}, not ${card.revision}`);
    }
    let sections = patchById(card.sections, delta.sections);
    for (const patch of delta.sectionItems ?? []) {
        let found = false;
        sections = sections.map((section) => {
            if (section.id !== patch.sectionId)
                return section;
            found = true;
            if (section.type !== patch.sectionType) {
                throw new Error(`Section ${patch.sectionId} is ${section.type}, not ${patch.sectionType}`);
            }
            if (section.type === "checklist" && patch.sectionType === "checklist") {
                return { ...section, items: patchById(section.items, patch.items) };
            }
            if (section.type === "choices" && patch.sectionType === "choices") {
                return { ...section, options: patchById(section.options, patch.items) };
            }
            if (section.type === "actions" && patch.sectionType === "actions") {
                return { ...section, actions: patchById(section.actions, patch.items) };
            }
            if (section.type === "verification" && patch.sectionType === "verification") {
                return { ...section, checks: patchById(section.checks, patch.items) };
            }
            return section;
        });
        if (!found)
            throw new Error(`Section ${patch.sectionId} does not exist`);
    }
    for (const patch of delta.milestoneUpdates ?? []) {
        let foundSection = false;
        let foundMilestone = false;
        sections = sections.map((section) => {
            if (section.id !== patch.sectionId)
                return section;
            foundSection = true;
            if (section.type !== "milestones") {
                throw new Error(`Section ${patch.sectionId} is ${section.type}, not milestones`);
            }
            return {
                ...section,
                items: section.items.map((milestone) => {
                    if (milestone.id !== patch.milestoneId)
                        return milestone;
                    foundMilestone = true;
                    let next = {
                        ...milestone,
                        items: patchById(milestone.items, patch.items),
                    };
                    if (patch.set?.title !== undefined)
                        next = { ...next, title: patch.set.title };
                    if (patch.set?.state !== undefined)
                        next = { ...next, state: patch.set.state };
                    if (patch.set && "detail" in patch.set) {
                        if (patch.set.detail === null) {
                            const { detail: _detail, ...withoutDetail } = next;
                            next = withoutDetail;
                        }
                        else if (patch.set.detail !== undefined) {
                            next = { ...next, detail: patch.set.detail };
                        }
                    }
                    return next;
                }),
            };
        });
        if (!foundSection)
            throw new Error(`Section ${patch.sectionId} does not exist`);
        if (!foundMilestone) {
            throw new Error(`Milestone ${patch.milestoneId} does not exist in ${patch.sectionId}`);
        }
    }
    return agentCardSchema.parse({
        ...card,
        ...delta.set,
        revision: delta.revision,
        sections,
    });
}
function patchById(current, patch) {
    if (!patch)
        return [...current];
    const removed = new Set(patch.remove ?? []);
    const items = new Map(current.filter((item) => !removed.has(item.id)).map((item) => [item.id, item]));
    for (const item of patch.upsert ?? [])
        items.set(item.id, item);
    return [...items.values()];
}
function uniqueIds(values, context, path) {
    const seen = new Set();
    for (const [index, value] of values.entries()) {
        if (seen.has(value.id)) {
            context.addIssue({
                code: "custom",
                path: [...path, index, "id"],
                message: `Duplicate id ${value.id}`,
            });
        }
        seen.add(value.id);
    }
}
//# sourceMappingURL=cards.js.map